<script setup lang="ts">
const phoneImages = {
  en: 'https://resources1.interface003.com/web30/2026/06/15/341d042d-01d2-42c8-b604-28c34c4546bc.png',
  traditional: 'https://resources1.interface003.com/web30/2026/06/15/33f9a3d0-0401-4f74-ae29-8f3dc4935fba.png',
  simplified: 'https://resources1.interface003.com/web30/2026/06/15/cf7df533-b9df-4ee2-8697-f4f9d07bb43f.png',
} as const
const logo = 'https://resources1.interface003.com/web30/2026/06/04/88f697d7-b19d-46fa-9f74-07f7d2dc31c1.png'

const traditionalChineseLocales = new Set(['zh-hk', 'zh-tw'])

const props = defineProps<{
  show: boolean
  traderData?: any
  traderCode?: string
  guaranteePop?: Partial<GuaranteePopData>
}>()
const { locale, t, localePath } = useAppI18n()
const show = computed(() => props.show)
const phoneImage = computed(() => {
  const normalizedLocale = String(locale).toLowerCase()

  if (normalizedLocale === 'zh-cn') {
    return phoneImages.simplified
  }

  if (traditionalChineseLocales.has(normalizedLocale)) {
    return phoneImages.traditional
  }

  return phoneImages.en
})
const traderName = computed(() => props.traderData?.basicInfo?.shortNameLocal)
const appStore = useAppStore()

const qrCodeLink = computed(() => {
  return `https://apph5.tech009.com/mobile/download?traderCode=${props.traderCode || ''}&type=22&contryCode=${appStore.CountryCode}&LanguageCode=${appStore.LanguageCode}`
})

const titleRef = ref<HTMLElement | null>(null)
const measureTitleSpanRef = ref<HTMLElement | null>(null)

// 1. SSR: Estimate text width based on CJK/Latin to avoid initial flash
const getInitialShowFullName = () => {
  if (props.guaranteePop?.type !== 0) return false
  const name = traderName.value || ''
  const fullText = t('83728', [name])

  let approxWidth = 0
  for (let i = 0; i < fullText.length; i++) {
    const code = fullText.charCodeAt(i)
    if (
      (code >= 0x4e00 && code <= 0x9fff) ||
      (code >= 0x3040 && code <= 0x30ff) ||
      (code >= 0xac00 && code <= 0xd7a3)
    ) {
      approxWidth += 24 // Font size is 24px here
    } else {
      approxWidth += 12 // roughly half
    }
  }
  return approxWidth <= 260
}

const showFullName = ref(getInitialShowFullName())

let resizeObserver: ResizeObserver | null = null

// 2. Client: Exact DOM measurement to correct the guess
const checkOverflow = () => {
  if (!measureTitleSpanRef.value) return
  showFullName.value = measureTitleSpanRef.value.scrollWidth <= measureTitleSpanRef.value.clientWidth
}

interface GuaranteePopData {
  topText: string
  nextStepTip: string
  isIdentityVerified: boolean
  needIdentityVerify: boolean
  verifyTipTitle: string
  verifyTipBottomText: string
  isWalletBound: boolean
  needWalletBind: boolean
  walletTipTitle: string
  walletTipBottomText: string
  type: number
  guardIcon: string
  guardName: string
  tags: any
  score: string
  cardTopText: string
  cardIcon: string
  compensationAmount: string
  compensationAmountColor: string
  guardStatusText: string
  guardStatusTextColor: string
  guardStatusType: number
  guardStatusBgColor: string
  guardStatusIcon: string
  gradientBgColor1: string
  gradientBgColor2: string
  listData: {
    title: string
    content: string
    contentColor: string
  }[]
  listTextData: string[]
  iapTermsUrl: string
  iapTermsText: string
  bottomButtons: {
    text: string
    textColor: string
    bgColor: string
    borderColor: string
    type: number
    nextStepTip: string
    reasonForFailure: string
  }[]
}

const guaranteePop = computed(() => props.guaranteePop || {})

const emit = defineEmits<{
  close: []
}>()

const dialogMask = useTemplateRef<HTMLElement>('dialogMask')
const { lock: lockBackgroundScroll, unlock: unlockBackgroundScroll } = useStickyDialogScrollLock()

const close = () => {
  emit('close')
}

watch(
  () => props.show,
  async (value) => {
    if (!import.meta.client) return

    if (value) {
      lockBackgroundScroll()
      await nextTick()
      dialogMask.value?.focus()

      if (props.guaranteePop?.type === 0) {
        checkOverflow()
        if (!resizeObserver) {
          resizeObserver = new ResizeObserver(() => checkOverflow())
        }
        if (titleRef.value) {
          resizeObserver.observe(titleRef.value)
        }
      }
      return
    }

    if (resizeObserver) {
      resizeObserver.disconnect()
      resizeObserver = null
    }
    unlockBackgroundScroll()
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  unlockBackgroundScroll()
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition name="iap-dialog-fade">
      <section v-if="show" ref="dialogMask" class="iap-dialog-mask" tabindex="-1" @keydown.esc="close">
        <div class="iap-dialog" role="dialog" aria-modal="true" aria-labelledby="iap-dialog-title">
          <section class="iap-dialog__content">
            <h2 id="iap-dialog-title" class="iap-dialog__title relative" ref="titleRef" style="min-width: 0">
              <span
                :style="{
                  color: guaranteePop?.type === 0 ? '#1f845a' : '#112D73',
                }"
                style="flex-shrink: 0"
                >IAP</span
              >
              <span
                v-if="guaranteePop?.type === 0"
                style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap"
                >{{ t('83728', showFullName ? [traderName || ''] : []) }}</span
              >
              <span v-else style="flex-shrink: 0">{{ t('83729') }}</span>
              <span
                class="iap-dialog__title-guard-status"
                :style="{
                  color: formatColor(guaranteePop?.guardStatusTextColor),
                  backgroundColor: formatColor(guaranteePop?.guardStatusBgColor),
                  flexShrink: 0,
                }"
                >{{ guaranteePop?.guardStatusText }}</span
              >
              <!-- Hidden measurement clone -->
              <div
                class="pointer-events-none absolute opacity-0"
                style="
                  min-width: 0;
                  flex: 1;
                  left: 0;
                  top: 0;
                  width: 100%;
                  display: flex;
                  align-items: center;
                  gap: 4px;
                  z-index: -1;
                "
                aria-hidden="true"
              >
                <span style="flex-shrink: 0">IAP</span>
                <span
                  v-if="guaranteePop?.type === 0"
                  ref="measureTitleSpanRef"
                  style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1"
                  >{{ t('83728', [traderName || '']) }}</span
                >
                <span v-else style="flex-shrink: 0">{{ t('83729') }} </span>
                <span
                  class="iap-dialog__title-guard-status"
                  style="flex-shrink: 0; border: 1px solid transparent; padding: 2px 8px"
                  >{{ guaranteePop?.guardStatusText }}</span
                >
              </div>
            </h2>

            <div class="iap-dialog__fund">
              <CommonImage
                v-if="guaranteePop?.cardIcon"
                :src="guaranteePop?.cardIcon"
                alt=""
                width="32"
                height="32"
                class="self-center rounded-full"
              />
              <strong>{{ guaranteePop?.compensationAmount }}</strong>
              <span>{{ guaranteePop?.fundLabel }}</span>
            </div>

            <dl
              class="iap-dialog__facts"
              :style="{
                '--gradient-bg-1': formatColor(guaranteePop?.gradientBgColor1) || '#eaf6f1',
                '--gradient-bg-2': formatColor(guaranteePop?.gradientBgColor2) || '#fff',
              }"
            >
              <div v-for="item in guaranteePop.listData">
                <dt>{{ item.title }}</dt>
                <dd :style="{ color: formatColor(item.contentColor) }">{{ item.content }}</dd>
              </div>
            </dl>

            <ol class="iap-dialog__rules">
              <li v-for="item in guaranteePop?.listTextData">
                {{ item }}
              </li>
            </ol>

            <p class="iap-dialog__terms">
              <span>{{ t('83887') }}</span>
              <NuxtLink :to="localePath({ name: 'iap' })" target="_blank" rel="noopener noreferrer">{{
                guaranteePop?.iapTermsText || '《 IAP·WikiBit守护服务条款 》'
              }}</NuxtLink>
            </p>
          </section>

          <aside class="iap-dialog__promotion">
            <!-- <CommonImage class="iap-dialog__promotion-background" :src="PanelBackground" alt="" /> -->

            <button
              type="button"
              class="iap-dialog__close right-[32px] rtl:right-auto rtl:left-[32px]"
              aria-label="关闭 IAP 守护弹窗"
              @click="close"
            >
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/06/15/3ac7ee61-9d42-44d2-bd6d-3a442b2803fd.png"
                alt=""
              />
            </button>

            <div class="iap-dialog__promotion-copy">
              <h3>{{ t('83906') }}<br />{{ t('83907') }}</h3>
              <p>
                <NuxtLink :to="localePath({ name: 'download' })" target="_blank" rel="noopener noreferrer">
                  {{ t('83891') }}<strong class="ml-[4px]">{{ t('83892') }}</strong>
                </NuxtLink>
              </p>
            </div>

            <div class="iap-dialog__qr">
              <div class="qr-container">
                <CommonQrCode class="qr-code" :text="qrCodeLink" :size="104" />
                <CommonImage class="qr-logo" :src="logo" alt="WikiBit" />
              </div>

              <span>{{ t('83893') }}</span>
            </div>

            <CommonImage class="iap-dialog__phone" :src="phoneImage" alt="WikiBit APP 界面展示" />
          </aside>
        </div>
      </section>
    </Transition>
  </Teleport>
</template>

<style scoped lang="less">
.iap-dialog-mask {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(9, 20, 51, 0.48);
  padding: 32px;
  outline: none;
}

.iap-dialog {
  display: flex;
  width: min(849px, calc(100vw - 48px));
  height: min(616px, calc(100vh - 64px));
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(8, 19, 62, 0.18);
}

.iap-dialog__content {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  padding: 48px;
}

.iap-dialog__title {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 0 32px;
  color: #292a2e;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
}

.iap-dialog__title span:first-child {
  color: #1f845a;
}

.iap-dialog__title-guard-status {
  padding: 4px 8px;
  border-radius: 2px;
  font-size: 12px;
  line-height: normal;
}

.iap-dialog__fund {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 24px;
}

.iap-dialog__fund strong {
  color: #292a2e;
  font-family: WikiFX, Outfit, sans-serif;
  font-size: 32px;
  font-weight: 700;
  line-height: 38px;
}

.iap-dialog__fund span {
  color: #7d818a;
  font-size: 14px;
  line-height: 22px;
}

.iap-dialog__facts {
  position: relative;
  overflow: hidden;
  margin: 0 0 24px;
  border-radius: 8px 8px 0 0;
  background: linear-gradient(180deg, var(--gradient-bg-1) 0%, var(--gradient-bg-2) 100%);
  padding: 0 16px;
  box-shadow: inset 0 4px 5px rgba(255, 255, 255, 0.55);
}

.iap-dialog__facts::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 8px 8px 0 0;
  padding: 1px;
  background: linear-gradient(180deg, var(--gradient-bg-1) 0%, var(--gradient-bg-2) 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

.iap-dialog__facts div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  font-size: 14px;
  line-height: 22px;
}

.iap-dialog__facts dt {
  color: #505258;
  font-weight: 400;
}

.iap-dialog__facts dd {
  margin: 0;
  color: #292a2e;
  font-weight: 500;
}

.iap-dialog__facts .iap-dialog__level {
  color: #1f845a;
}

.iap-dialog__rules {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  flex-direction: column;
  gap: 20px;
  margin: 0;
  padding: 0;
  color: #292a2e;
  font-size: 14px;
  line-height: 22px;
  list-style-position: inside;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.iap-dialog__terms {
  margin-top: 34px;
  color: #91969c;
  font-size: 14px;
  line-height: 22px;
  text-align: center;
}

.iap-dialog__terms a {
  color: #112d73;
  text-decoration: none;
}

.iap-dialog__promotion {
  position: relative;
  width: 361px;
  flex: 0 0 auto;
  overflow: hidden;
  background: #07132f;
  color: #fff;
}

.iap-dialog__promotion-background {
  position: absolute;
  width: 900px;
  height: 616px;
  max-width: none;
  inset: 0 auto 0 50%;
  object-fit: cover;
  transform: translateX(-50%);
}

.iap-dialog__close {
  position: absolute;
  z-index: 3;
  top: 32px;
  display: flex;
  width: 24px;
  height: 24px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: #fff;
  padding: 0;
}

.iap-dialog__close :deep(svg) {
  width: 24px;
  height: 24px;
}

.iap-dialog__promotion-copy {
  position: absolute;
  z-index: 2;
  top: 56px;
  left: 0;
  width: 100%;
  text-align: center;
}

.iap-dialog__promotion-copy h3 {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
}

.iap-dialog__promotion-copy p {
  margin: 0;
  color: #b7b9be;
  font-size: 14px;
  line-height: 22px;
}

.iap-dialog__promotion-copy strong {
  color: #fff;
  font-weight: 400;
}

.iap-dialog__qr {
  position: absolute;
  z-index: 2;
  top: 168px;
  left: 48px;
  display: flex;
  width: 265px;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #b7b9be;
  font-size: 14px;
  line-height: 22px;
  .qr-container {
    position: relative;
    display: flex;
    width: 120px;
    height: 120px;
    box-sizing: border-box;
    padding: 7px;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1/1;
    border-radius: 12px;
    border: 1px solid rgba(11, 18, 40, 0.14);
    background: #fff;
  }
}

.iap-dialog__qr .qr-code {
  width: 104px;
  height: 104px;
  flex: 0 0 auto;

  :deep(img),
  :deep(canvas) {
    display: block;
    width: 104px !important;
    height: 104px !important;
    image-rendering: pixelated;
  }
}

.iap-dialog__qr .qr-logo {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
  width: 32px;
  height: 32px;
  box-sizing: border-box;
  border: 1px solid #fff;
  border-radius: 6px;
  object-fit: cover;
  pointer-events: none;
  transform: translate(-50%, -50%);
}

.iap-dialog__phone {
  position: absolute;
  z-index: 1;
  top: 285px;
  width: 330px;
  max-width: none;
}

.iap-dialog-fade-enter-active,
.iap-dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.iap-dialog-fade-enter-active .iap-dialog,
.iap-dialog-fade-leave-active .iap-dialog {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.iap-dialog-fade-enter-from,
.iap-dialog-fade-leave-to {
  opacity: 0;
}

.iap-dialog-fade-enter-from .iap-dialog,
.iap-dialog-fade-leave-to .iap-dialog {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

@media screen and (max-width: 799px) {
  .iap-dialog-mask {
    justify-content: flex-start;
    overflow-x: auto;
  }

  .iap-dialog {
    width: 849px;
    min-width: 849px;
    height: 616px;
    flex-shrink: 0;
  }
}
</style>
